import React, { useEffect, useState, useCallback } from "react";
import { authFetch, C } from "./App";

const SEV_COLOR = { critical: "#9C27B0", high: "#F44336", medium: "#FF9800", low: "#4CAF50" };
const MOD_BADGE = {
  approved: { bg: "#E8F5E9", color: "#2E7D32", label: "Approved" },
  pending:  { bg: "#FFF8E1", color: "#B8860B", label: "Pending" },
  rejected: { bg: "#FFEBEE", color: "#C62828", label: "Rejected" },
};

export function IncidentsView({ token }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [err, setErr] = useState("");
  const [actingId, setActingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = filter !== "all" ? `?status=${filter}` : "";
    authFetch(`/admin/incidents${q}`, token)
      .then((d) => setIncidents(d.incidents || []))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  function act(id, action) {
    setActingId(id);
    const call = action === "delete"
      ? authFetch(`/admin/incidents/${id}`, token, { method: "DELETE" })
      : authFetch(`/admin/incidents/${id}/${action}`, token, { method: "PATCH" });
    call
      .then(() => {
        if (action === "delete") setIncidents((prev) => prev.filter((i) => i.id !== id));
        else load();
      })
      .catch((e) => setErr(e.message))
      .finally(() => setActingId(null));
  }

  const visible = incidents.filter((i) =>
    !search || (i.area || "").toLowerCase().includes(search.toLowerCase()) || (i.type || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111", margin: "0 0 4px" }}>Incident Moderation</h1>
      <p style={{ fontSize: 13, color: C.gray, margin: "0 0 20px" }}>Approve, reject, or remove reported incidents</p>

      {err && <div style={{ color: C.danger, fontSize: 13, marginBottom: 14 }}>{err}</div>}

      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <input
          value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by area or type…"
          style={{ flex: "1 1 220px", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13 }}
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13 }}>
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div style={{ color: C.gray, fontSize: 13 }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ color: C.gray, fontSize: 13, padding: 30, textAlign: "center", background: C.white, borderRadius: 12, border: `1px solid ${C.border}` }}>No incidents match this filter.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visible.map((inc) => {
            const badge = MOD_BADGE[inc.mod_status] || MOD_BADGE.approved;
            const sc = SEV_COLOR[inc.severity] || "#607D8B";
            return (
              <div key={inc.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 4, height: 38, background: sc, borderRadius: 2, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{inc.type}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: sc, background: sc + "1A", padding: "1px 7px", borderRadius: 8 }}>{(inc.severity || "").toUpperCase()}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: badge.color, background: badge.bg, padding: "1px 7px", borderRadius: 8 }}>{badge.label}</span>
                  </div>
                  <div style={{ fontSize: 12, color: C.gray }}>{inc.area} · {new Date(inc.created_at || inc.date).toLocaleDateString()}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {inc.mod_status !== "approved" && (
                    <ActBtn onClick={() => act(inc.id, "verify")} disabled={actingId === inc.id} color={C.success}>Approve</ActBtn>
                  )}
                  {inc.mod_status !== "rejected" && (
                    <ActBtn onClick={() => act(inc.id, "reject")} disabled={actingId === inc.id} color={C.warn}>Reject</ActBtn>
                  )}
                  <ActBtn onClick={() => { if (window.confirm("Permanently delete this incident?")) act(inc.id, "delete"); }} disabled={actingId === inc.id} color={C.danger}>Delete</ActBtn>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActBtn({ onClick, disabled, color, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ background: "transparent", border: `1px solid ${color}55`, color, borderRadius: 7, padding: "6px 11px", fontSize: 11.5, fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 }}>
      {children}
    </button>
  );
}
